import { IItemList } from '@components/GlobalMenu/GlobalMenu';
import { DeleteDialog, GlobalMenu } from '@components/index';
import { useHookstate } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { IDocumentTable } from '@models/Document.model';
import { EditDocument } from '@pages/Asset/components/Documents/Components/EditDocument/EditDocument';
import { deleteDocument } from '@services/queries/documents';
import { getPermissionData } from '@utils/index';
import { noop } from '@utils/noop';
import { useContext } from 'react';
import { useMutation } from 'react-query';

interface EditDocumentParams {
  document: IDocumentTable;
  invalidate: VoidFunction;
}

export function EditDocumentMenu(props: EditDocumentParams) {
  const { document, invalidate = noop } = props;
  const { role } = useContext(NavigationContext);

  const openEditDocument = useHookstate(false);

  const openDeleteDialog = useHookstate(false);

  const editPermission = getPermissionData({
    owner: Boolean(document.ownerStudioId),
    action: 'edit',
    role,
    type: 'Document',
  });
  const deletePermission = getPermissionData({
    owner: Boolean(document.ownerStudioId),
    action: 'delete',
    role,
    type: 'Document',
  });

  const mutation = useMutation(deleteDocument, {
    onSuccess: () => {
      invalidate();
      openDeleteDialog.set(false);
    },
  });
  const documentManufacturer =
    document.manufacturer || document.product?.manufacturer;

  function hasManufacturer() {
    if (document.productManufacturerId) return true;
    return !!document.manufacturerId;
  }
  function getEditAllowed() {
    if (!editPermission.allowed) {
      return {
        allowed: false,
        message: editPermission.message,
      };
    }

    return {
      allowed: !!hasManufacturer(),
      message: 'Manufacturer missing',
    };
  }
  function getItems(): IItemList[] {
    return [
      {
        disabled: !getEditAllowed().allowed,
        disableToolTip: getEditAllowed().allowed,
        label: 'Edit',
        iconName: 'edit',
        toolTipText: getEditAllowed().message,
        toolTipId: 'editDocumentTooltip',
        onClick: () => {
          if (getEditAllowed().allowed) {
            openEditDocument.set(true);
          }
        },
      },
      {
        label: 'Delete',
        iconName: 'trash',
        disabled: !deletePermission.allowed,
        toolTipText: deletePermission.message,
        toolTipId: 'deleteDocumentTooltip',
        disableToolTip: deletePermission.allowed,
        onClick: () => {
          if (deletePermission.allowed) {
            openDeleteDialog.set(true);
          }
        },
      },
    ];
  }

  return (
    <>
      <GlobalMenu
        hoverColor="var(--beam-color-neutral-n300)"
        align="end"
        title="Options"
        itemList={getItems()}
      />
      {openDeleteDialog.get() && (
        <DeleteDialog
          open={openDeleteDialog}
          onDelete={() => {
            mutation.mutate(document.id);
          }}
        />
      )}
      {openEditDocument.get() && documentManufacturer && (
        <EditDocument
          manufacturerData={documentManufacturer}
          productData={document.product}
          openEditDocument={openEditDocument}
          selectedDocument={document}
          onSuccess={() => invalidate()}
        />
      )}
    </>
  );
}
